/**
 * Button — themeable action button (Claude Design `.btn`).
 *
 * Variants: primary | secondary | ghost | danger | danger-ghost.
 * Sizes: sm (27px) | md (32px, default) | lg (40px) | icon (32² square) | block.
 *
 * Pure Tailwind (no Radix). Uses the semantic color tokens from theme.css so it
 * adapts to dark/light automatically. The primary button carries the design's
 * brand shadow and lifts on hover (`-translate-y-px` + brightness).
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './cn.js';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'block';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const base =
  'inline-flex items-center justify-center gap-[7px] whitespace-nowrap leading-none ' +
  'rounded-md border border-transparent font-semibold transition select-none cursor-pointer ' +
  '[&_svg]:size-[15px] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
  'disabled:cursor-default disabled:opacity-45 disabled:!translate-y-0 disabled:!brightness-100';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-ink shadow-[0_1px_0_rgb(0_0_0/0.06),0_6px_18px_-8px_var(--hl-primary)] ' +
    'hover:brightness-110 hover:-translate-y-px',
  secondary:
    'bg-surface-2 border-border text-text hover:border-border-strong hover:bg-surface-3',
  ghost: 'bg-transparent text-muted hover:bg-surface-hover hover:text-text',
  danger: 'bg-danger text-white hover:brightness-110',
  'danger-ghost': 'bg-danger-soft text-danger hover:brightness-105',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-[27px] px-2.5 text-xs rounded-sm',
  md: 'h-8 px-[13px] text-[13px]',
  lg: 'h-10 px-[18px] text-sm',
  icon: 'h-8 w-8 px-0 text-[13px]',
  block: 'h-8 w-full px-[13px] text-[13px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      data-variant={variant}
      className={cn(base, variants[variant], sizes[size], className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
